<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"
import { computed } from "vue"
import type { Subscription } from "@modules/espace-entreprise/types/subscription"
import { useAuthStore } from "@core/stores/auth.store"
import { useRedirect } from "@core/composables/useRedirect"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Abonnements et suivis",
            empty: "Aucun abonnement disponible",
            not_client:
                "Vous n'avez pas encore d'abonnement. Abonnez-vous pour accéder à nos services.",
            subscribe: "S'abonner",
        },
    },
})

// ================================
// STORES
// ================================
const { redirectWrapper } = useRedirect()
const authStore = useAuthStore()

// ================================
// COMPUTED
// ================================

/**
 * Indique si l'entreprise est un client Homeys
 */
const getIsClientHomeys = computed(() => {
    return authStore.isClientHomeys
})

/**
 * Liste des abonnements de l'entreprise (depuis le store auth)
 */
const getSubscriptions = computed((): Subscription[] => {
    return (
        (authStore.companyStripeSubscriptions as Subscription[] | undefined) ??
        []
    )
})

/**
 * Indique si la liste d'abonnements est vide
 */
const getIsSubscriptionsEmpty = computed(() => {
    return !getSubscriptions.value.length
})

/**
 * Indique si les données utilisateur/entreprise sont en cours de chargement (fetch user)
 */
const getIsLoading = computed(() => authStore.isFetchingUser)

// ================================
// INTERACTIONS
// ================================

/**
 * Redirige vers la page d'inscription
 */
function onRedirectToSubscription(): void {
    redirectWrapper("/inscription")
}
</script>

<template>
    <BaseTitledCard>
        <template #header>
            {{ t("title") }}
        </template>
        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template #content>
            <div class="w-full">
                <!-- ================================ -->
                <!-- CAS : CLIENT HOMEYS -->
                <!-- ================================ -->
                <template v-if="getIsClientHomeys">
                    <!-- Skeleton pendant le chargement user -->
                    <BaseSkeleton
                        v-if="getIsLoading"
                        width="100%"
                        height="5rem"
                        class="mb-4"
                    />

                    <!-- Liste des abonnements -->
                    <CompanySubscriptionsList
                        v-else-if="!getIsSubscriptionsEmpty"
                        :subscriptions="getSubscriptions"
                    />

                    <!-- Message si liste vide -->
                    <BaseMessage v-else severity="info" class="w-full">
                        {{ t("empty") }}
                    </BaseMessage>
                </template>

                <!-- ================================ -->
                <!-- CAS : NON CLIENT HOMEYS -->
                <!-- ================================ -->
                <BaseMessage v-else severity="info" class="w-full">
                    <template #default>
                        <div class="flex flex-col gap-4">
                            <p>{{ t("not_client") }}</p>
                            <BaseButton
                                :label="t('subscribe')"
                                @click="onRedirectToSubscription"
                            />
                        </div>
                    </template>
                </BaseMessage>
            </div>
        </template>
    </BaseTitledCard>
</template>
